import type { Poll } from '../types';

/** Normalize role strings from API/cache (handles spacing or casing drift). */
export function normalizeRole(role?: string | null): string {
  return String(role ?? '')
    .trim()
    .toUpperCase();
}

export function isAdminRole(role?: string | null): boolean {
  return normalizeRole(role) === 'ADMIN';
}

/** Co-Admin or Admin — user/policy/events management, sidebar edit, etc. */
export function isCoAdminOrAdmin(role?: string | null): boolean {
  const r = normalizeRole(role);
  return r === 'ADMIN' || r === 'CO_ADMIN';
}

/** Admin / Co-Admin / Content uploader — see all posts in API; may use legacy department tabs. */
export function isElevatedFeedRole(role?: string | null): boolean {
  const r = normalizeRole(role);
  return r === 'ADMIN' || r === 'CO_ADMIN' || r === 'CONTENT_UPLOADER';
}

/** Employee / branch admin / department admin — feed scoped by API + My Branch / My Department. */
export function isRestrictedFeedRole(role?: string | null): boolean {
  const r = normalizeRole(role);
  return r === 'EMPLOYEE' || r === 'BRANCH_ADMIN' || r === 'DEPARTMENT_ADMIN';
}

/** Sidebar spotlight (Employee of the Month / New employee) — Co-Admin, Admin, or Content uploader. */
export function canManageSpotlightPosts(role?: string | null): boolean {
  const r = normalizeRole(role);
  return r === 'ADMIN' || r === 'CO_ADMIN' || r === 'CONTENT_UPLOADER';
}

/** Who may create posts in the feed (UI + intended use). */
export function canCreatePosts(role?: string | null): boolean {
  const r = normalizeRole(role);
  return (
    r === 'ADMIN' ||
    r === 'CO_ADMIN' ||
    r === 'CONTENT_UPLOADER' ||
    r === 'BRANCH_ADMIN' ||
    r === 'DEPARTMENT_ADMIN'
  );
}

/** Polls: same as posts — all roles except employees. */
export function canCreatePolls(role?: string | null): boolean {
  return canCreatePosts(role);
}

/** End polls and view final tallies (Admin / Co-Admin). */
export function canEndPolls(role?: string | null): boolean {
  return isCoAdminOrAdmin(role);
}

/** Edit or delete polls: creator, or Admin / Co-Admin (matches backend). */
export function canManagePoll(
  poll: Pick<Poll, 'createdBy'>,
  viewerId?: string | null,
  viewerRole?: string | null,
): boolean {
  const creatorId = poll.createdBy?.id;
  if (creatorId && viewerId && creatorId === viewerId) return true;
  return isCoAdminOrAdmin(viewerRole);
}

/** Employees and content uploaders must accept policies; admins bypass. */
export function needsPolicyAcceptance(role?: string | null): boolean {
  const r = normalizeRole(role);
  return (
    r === 'EMPLOYEE' ||
    r === 'CONTENT_UPLOADER' ||
    r === 'BRANCH_ADMIN' ||
    r === 'DEPARTMENT_ADMIN'
  );
}

/** Human-readable role for UI (stored values stay CO_ADMIN, CONTENT_UPLOADER, etc.). */
export function formatRoleLabel(role: string): string {
  switch (normalizeRole(role)) {
    case 'ADMIN':
      return 'Admin';
    case 'CO_ADMIN':
      return 'Co-Admin';
    case 'CONTENT_UPLOADER':
      return 'Content uploader';
    case 'BRANCH_ADMIN':
      return 'Branch admin';
    case 'DEPARTMENT_ADMIN':
      return 'Department admin';
    case 'EMPLOYEE':
      return 'Employee';
    default:
      return role;
  }
}
